/**
 * IntakeFilters Component
 * Handles client selection and date navigation.
 * The date picker is a calendar popover that shows a green dot below days
 * that have at least one food intake record (non-empty plans only).
 */

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { trpc } from "@/lib/trpc";
import type { Klient } from "../types";

interface IntakeFiltersProps {
  selectedKlientId: number | null;
  onKlientChange: (klientId: number | null) => void;
  selectedDate: string;
  onDateChange: (date: string) => void;
  klienti: Klient[];
}

export function IntakeFilters({
  selectedKlientId,
  onKlientChange,
  selectedDate,
  onDateChange,
  klienti,
}: IntakeFiltersProps) {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Track which month is displayed in the calendar so we can fetch the right set of days
  const [displayMonth, setDisplayMonth] = useState<Date>(() =>
    selectedDate ? parseISO(selectedDate) : new Date()
  );

  const yearMonth = useMemo(
    () => format(displayMonth, "yyyy-MM"),
    [displayMonth]
  );

  // Fetch days that have at least one prijem record for the displayed month.
  // The server query already guarantees only non-empty days are returned
  // (it selects DISTINCT dates from the prijem table).
  const { data: daysWithPrijem = [] } = trpc.prijem.getDaysWithPrijem.useQuery(
    { klientId: selectedKlientId!, yearMonth },
    { enabled: !!selectedKlientId }
  );

  // Build a Set for O(1) lookup
  const plannedDays = useMemo(() => new Set(daysWithPrijem), [daysWithPrijem]);

  const selectedDateObj = selectedDate ? parseISO(selectedDate) : undefined;

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    onDateChange(date.toISOString().split("T")[0]);
    setDisplayMonth(date);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    onDateChange(date.toISOString().split("T")[0]);
    setDisplayMonth(date);
  };

  const handleCalendarSelect = (day: Date | undefined) => {
    if (!day) return;
    onDateChange(format(day, "yyyy-MM-dd"));
    setCalendarOpen(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* Client selector */}
      <div className="space-y-2">
        <Label>{t("intake:filters.filterByClient")}</Label>
        <Select
          value={selectedKlientId?.toString() || ""}
          onValueChange={value => onKlientChange(Number(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("intake:filters.filterByClient")} />
          </SelectTrigger>
          <SelectContent>
            {klienti.map(k => (
              <SelectItem key={k.id} value={k.id.toString()}>
                {k.alias} - {k.meno} {k.priezvisko}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date navigation with calendar popover */}
      <div className="space-y-2">
        <Label>{t("common:date")}</Label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePreviousDay}
            disabled={!selectedKlientId}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 justify-between font-normal"
                disabled={!selectedKlientId}
              >
                {selectedDate
                  ? format(parseISO(selectedDate), "d. M. yyyy")
                  : t("intake:selectDate")}
                <CalendarIcon className="ml-2 h-4 w-4 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDateObj}
                onSelect={handleCalendarSelect}
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                modifiers={{
                  hasIntake: day => plannedDays.has(format(day, "yyyy-MM-dd")),
                }}
                modifiersClassNames={{
                  // Use a relative wrapper approach: add a class that the CSS
                  // targets to render a small green dot below the day number.
                  // We use a Tailwind arbitrary-variant trick via a data attribute
                  // injected through the className — the dot is rendered via
                  // ::after pseudo-element styled in index.css.
                  hasIntake: "day-has-intake",
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="icon"
            onClick={handleNextDay}
            disabled={!selectedKlientId}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Quick action: jump to today */}
      <div className="space-y-2">
        <Label>{t("intake:filters.quickActions", "Quick Actions")}</Label>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            const today = new Date().toISOString().split("T")[0];
            onDateChange(today);
            setDisplayMonth(new Date());
          }}
          disabled={!selectedKlientId}
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          {t("intake:filters.today", "Today")}
        </Button>
      </div>
    </div>
  );
}
